import { useState, useEffect, useCallback } from "react";
import { jobRoleApi } from "../services/api";
import { useTenantContext } from "../contexts/TenantContext";

/**
 * Helper to extract data from API response
 * Handles both { success, data } and { Success, Data } formats
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };

  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  const message = response.message ?? response.Message ?? null;

  return { success, data, message };
};

/**
 * Hook for fetching and managing job roles
 */
export function useJobRoles(options = {}) {
  const {
    activeOnly = false,
    troncEligibleOnly = false,
    autoFetch = true,
    tenantId = null,
  } = options;
  const { selectedTenant, isGlobalAdmin } = useTenantContext();

  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine which tenant ID to use
  const effectiveTenantId =
    tenantId || (isGlobalAdmin ? selectedTenant?.id : null);

  const fetchJobRoles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (activeOnly) params.activeOnly = true;
      if (troncEligibleOnly) params.troncEligibleOnly = true;

      // For global admins, pass the selected tenant ID
      if (effectiveTenantId) {
        params.tenantId = effectiveTenantId;
      }

      const response = await jobRoleApi.getAll(params);
      const { success, data, message } = extractData(response);

      if (success) {
        setJobRoles(data || []);
      } else {
        setError(message || "Failed to fetch job roles");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch job roles");
    } finally {
      setLoading(false);
    }
  }, [activeOnly, troncEligibleOnly, effectiveTenantId]);

  useEffect(() => {
    if (autoFetch) {
      fetchJobRoles();
    }
  }, [fetchJobRoles, autoFetch]);

  return {
    jobRoles,
    loading,
    error,
    refetch: fetchJobRoles,
  };
}

/**
 * Hook for job role mutations
 */
export function useJobRoleMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createJobRole = async (jobRoleData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await jobRoleApi.create(jobRoleData);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateJobRole = async (id, jobRoleData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await jobRoleApi.update(id, jobRoleData);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteJobRole = async (id, reason) => {
    setLoading(true);
    setError(null);

    try {
      const response = await jobRoleApi.delete(id, reason);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const syncFromProvider = async (providerName) => {
    setLoading(true);
    setError(null);

    try {
      const response = await jobRoleApi.syncFromProvider(providerName);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createJobRole,
    updateJobRole,
    deleteJobRole,
    syncFromProvider,
  };
}

export default useJobRoles;
